import './context-menu.less';
import { useLatest } from 'ahooks';
import classNames from 'classnames';
import { groupBy } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BaseSystemPlugin, getPointRect, useFramework, useViewSize } from '@/framework';
import { isLeftButton } from '@/utils/event-kit';
import { IBackgroundLayerProps, IMainLayerProps, IPoint } from '@/version-types';
import { ContextMenuPlugin } from '../context-menu';
import { getContextMenuState, useContextMenuState } from '../states/context-menu-state';
import MenuItem from './menu-item';

const ContextMenu: React.FC = React.memo(() => {
    const framework = useFramework();
    const menuRef = useRef<HTMLDivElement>(null);
    // 右键菜单信息
    const [menuState, setMenuState] = useState<{ isShow: boolean }>({ isShow: false });
    const contextMenuInfoRef = useRef(
        framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin').positionInfo,
    );
    const activeInfoRef = useRef(
        framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin').activeInfo,
    );
    const isShowRef = useLatest(menuState.isShow);
    const viewSize = useViewSize();
    const { width: viewWidth, height: viewHeight } = viewSize;
    const hiddenContextMenuTypes = useContextMenuState()((state) => state.hiddenContextMenuTypes);

    useEffect(() => {
        framework.service.frame.delHoverId();
        if (menuState.isShow) framework.service.formatBrush.cancel();
        framework.service.frame.clearSelected();
        framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin').setState(menuState.isShow);
    }, [menuState.isShow]);

    // 销毁右键菜单
    const destroyContextMenu = useCallback(() => {
        if (!isShowRef.current) return;
        // menuRef.current?.classList.add('destroy');
        activeInfoRef.current.activeLayer = null;
        activeInfoRef.current.pickLayer = null;
        activeInfoRef.current.isImageEditor = false;
        activeInfoRef.current.operateLayerType = '';
        setMenuState({ isShow: false });
        framework.service.editor.setContextMenuVisible(false);
    }, []);

    // 创建右键菜单
    const createContextMenu = useCallback((point: IPoint) => {
        if (getContextMenuState(framework.identity).disableMenu) return;
        contextMenuInfoRef.current.x = point.x;
        contextMenuInfoRef.current.y = point.y;
        contextMenuInfoRef.current.bottom = 0;
        contextMenuInfoRef.current.right = 0;
        const activeLayer = framework.service.frame.getActiveLayer();
        const pickLayer = framework.service.frame.getActivePickLayer();
        const originIndex = framework.service.frame.getActiveFrameId();
        const mainLayer = framework.service.frame.getMainLayer(originIndex);
        let operateLayerType = '';
        if (activeLayer) {
            const layer = pickLayer || activeLayer;
            operateLayerType = layer.layerType;
        } else {
            const backgroundLayer = framework.service.frame.getBackgroundLayer(originIndex);
            const backgroundLayerUrl = (backgroundLayer?.data as IBackgroundLayerProps)?.url;
            //主图不存在（海报编辑器） 或 主图（图片编辑器）存在并且是自定义画布且存在背景
            if (!mainLayer || (mainLayer && (mainLayer.data as IMainLayerProps).isTransparent && backgroundLayerUrl)) {
                operateLayerType = backgroundLayerUrl ? backgroundLayer?.layerType || '' : '';
            } else {
                operateLayerType = mainLayer.layerType;
            }
        }
        activeInfoRef.current.isImageEditor = !!mainLayer;
        activeInfoRef.current.operateLayerType = operateLayerType;
        activeInfoRef.current.activeLayer = activeLayer;
        activeInfoRef.current.pickLayer = pickLayer;
        setMenuState({ isShow: true });
        framework.service.editor.setContextMenuVisible(true);
        setTimeout(() => {
            if (!menuRef.current) return;
            const { bottom, right } = framework.service.editor.getBoundingClientRect();
            contextMenuInfoRef.current.bottom = bottom;
            contextMenuInfoRef.current.right = right;
            const { width, height } = menuRef.current.getBoundingClientRect();
            let { x, y } = contextMenuInfoRef.current;
            const menuSpace = getContextMenuState(framework.identity).menuSpace;
            // 处理底部溢出 和 右边溢出的情况
            if (x > right - width) {
                x = right - menuSpace - width;
            }
            contextMenuInfoRef.current.arrowMenuInRight =
                x <= right - getContextMenuState(framework.identity).arrowMenuInLeftSpace - width;
            if (y > bottom - height) {
                y = bottom - menuSpace - height;
            }
            menuRef.current.style.left = `${x}px`;
            menuRef.current.style.top = `${y}px`;
            menuRef.current.classList.remove('destroy');
        }, 0);
    }, []);

    // 监听视图宽高变化，销毁菜单
    useEffect(() => {
        destroyContextMenu();
    }, [viewWidth, viewHeight]);

    // 监听右键点击展示菜单
    useEffect(() => {
        const handler = framework.service.editor.onContextMenu((e: React.MouseEvent) => {
            if (getContextMenuState(framework.identity).disableMenu) return;
            const point = { x: e.pageX, y: e.pageY };
            const hoverId = framework.service.frame.getHoverId();
            if (hoverId) {
                framework.service.frame.setActiveLayer(hoverId);
            }
            const index = framework.service.transform.getFrameIndexByMousePointY(point.y);
            framework.service.frame.setActiveIndex(index);
            const inStage = framework.service.transform.checkPointInFrame(point, index);
            if (!inStage) {
                framework.service.frame.delActiveLayer();
            } else {
                const activeLayer = framework.service.frame.getActiveLayer();
                if (activeLayer) {
                    const mouseDownRect = getPointRect(framework.service.transform.getScenePoint(e));
                    const layerId = framework.service.transform.tryPickWrap(mouseDownRect, [activeLayer]);
                    const isInOperateArea = framework.service.behavior
                        .get(activeLayer)
                        ?.checkIsInOperateArea?.(activeLayer.id, point);
                    if (!layerId && !isInOperateArea) {
                        framework.service.frame.delActiveLayer();
                    }
                }
            }
            createContextMenu(point);
        });
        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    // 隐藏菜单组件
    useEffect(() => {
        const handler = framework.service.editor.onMouseDown((e) => {
            if (isLeftButton(e) && isShowRef.current) {
                destroyContextMenu();
                const hoverId = framework.service.frame.getHoverId();
                if (!hoverId) {
                    return true;
                }
            }
        }, 99);
        const clickHandler = (e: MouseEvent) => {
            const point = { x: e.pageX, y: e.pageY };
            const inViewport = framework.service.transform.checkPointInViewport(point);
            if (!inViewport && isShowRef.current) {
                destroyContextMenu();
            }
        };
        document.addEventListener('click', clickHandler);
        return () => {
            framework.service.events.off(handler);
            document.removeEventListener('click', clickHandler);
        };
    }, []);

    // 监听右键菜单关闭事件
    useEffect(() => {
        return framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin').onClose(() => {
            destroyContextMenu();
        });
    }, []);

    // 监听右键菜单打开事件
    useEffect(() => {
        return framework.service.registration.getInstance<ContextMenuPlugin>('ContextMenuPlugin').onShow((point) => {
            createContextMenu(point);
        });
    }, []);

    /** 获取自定义右键菜单 */
    const clinePoint = contextMenuInfoRef.current;
    const stagePoint = framework.service.transform.getViewportPoint(clinePoint);
    const customContextMenuData = getContextMenuState(framework.identity).getCustomContextMenu({
        stageX: stagePoint.x,
        stageY: stagePoint.y,
        clientX: clinePoint.x,
        clientY: clinePoint.y,
    });
    const customContextMenu: Record<number, React.ReactNode[]> = {};
    if (customContextMenuData) {
        customContextMenuData.forEach((item) => (item.group = item.group || 1));
        const data = groupBy(customContextMenuData, 'group');
        Object.keys(data)
            .map(parseFloat)
            .sort((a, b) => a - b)
            .forEach((group) => {
                customContextMenu[group] = customContextMenu[group] || [];
                data[group].forEach((item, i) => {
                    customContextMenu[group].push(<MenuItem key={`${group}-${i}`} {...item} />);
                });
            });
    }
    const contextMenuList = useContextMenuState()((state) => state.contextMenuList);
    /** 已注册右键菜单（内置项由状态初始化时注入，亦可通过 ContextMenuPlugin 替换/追加） */
    const defaultContextMenu: Record<number, React.ReactNode[]> = {};
    const defaultContextMenuItems = groupBy(contextMenuList, 'group');
    Object.keys(defaultContextMenuItems)
        .map(parseFloat)
        .sort((a, b) => a - b)
        .forEach((group) => {
            defaultContextMenu[group] = defaultContextMenu[group] || [];
            defaultContextMenuItems[group]
                .sort((a, b) => a.sort - b.sort)
                .forEach((item, i) => {
                    const node = item.render(activeInfoRef.current, framework.identity);
                    if (node && !hiddenContextMenuTypes.includes(node.type)) {
                        defaultContextMenu[group].push(<MenuItem {...node} key={item.key || `${group}-${i}`} />);
                        // defaultContextMenu[group].push(<React.Fragment key={`${group}-${i}`}>{node}</React.Fragment>);
                    }
                });
        });

    // console.log('groupData', { customContextMenu, defaultContextMenu });
    const renderMenu = customContextMenuData ? customContextMenu : defaultContextMenu;

    if (!menuState.isShow) return null;

    return (
        <BaseSystemPlugin name="context-menu" style={{ zIndex: 100 }}>
            <div className="l-content-menu-wrap">
                <div
                    ref={menuRef}
                    className={classNames('l-content-menu destroy', {
                        empty: !Object.keys(renderMenu).length,
                    })}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onMouseMove={(e) => e.stopPropagation()}
                >
                    {Object.keys(renderMenu).map((group, i) => {
                        return (
                            <React.Fragment key={group}>
                                {i && renderMenu[group]?.length ? <li className="l-content-menu__line"></li> : null}
                                {renderMenu[group]?.length ? renderMenu[group] : null}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </BaseSystemPlugin>
    );
});

export default ContextMenu;
